import React from "react"

import {
  isWebSocketConnectionMode,
  SimulatorType,
  WebSocketConnectionMode,
} from "@/types/api/SimulatorType"

import {
  AgentTestConnectionButton,
  type AgentTestConnectionConfig,
} from "../AgentTestConnectionButton"
import {
  AdvancedConfigSection,
  ConnectionModeToggle,
  DirectModeFields,
  HttpFirstSetupFields,
  HttpFirstWebSocketFields,
  TwimlWebhookFields,
  WebSocketPresetSelect,
} from "./websocket-config"
import {
  TWIML_WEBHOOK_MODE_METADATA,
  withTwimlWebhookRuntimeDefaults,
} from "./websocket-config/presets"
import type { WebSocketSimulatorConfigData } from "./websocket-config/types"

interface WebSocketSimulatorConfigProps {
  simulatorType: SimulatorType
  simulationMetadata: WebSocketSimulatorConfigData
  onMetadataUpdate: (metadata: Partial<WebSocketSimulatorConfigData>) => void
  disabled?: boolean
  testConnection?: AgentTestConnectionConfig | null
  hideAdvancedSettings?: boolean
}

export const WebSocketSimulatorConfig: React.FC<
  WebSocketSimulatorConfigProps
> = ({
  simulatorType,
  simulationMetadata,
  onMetadataUpdate,
  disabled = false,
  testConnection,
  hideAdvancedSettings = false,
}) => {
  const connectionMode = isWebSocketConnectionMode(
    simulationMetadata.connection_mode,
  )
    ? simulationMetadata.connection_mode
    : WebSocketConnectionMode.DIRECT
  const isHttpFirst = connectionMode === WebSocketConnectionMode.HTTP_FIRST
  const isTwimlWebhook =
    connectionMode === WebSocketConnectionMode.TWIML_WEBHOOK
  const effectiveSimulationMetadata = isTwimlWebhook
    ? withTwimlWebhookRuntimeDefaults(simulationMetadata)
    : simulationMetadata

  if (simulatorType !== SimulatorType.WEBSOCKET) {
    return null
  }

  return (
    <div className="space-y-4">
      <ConnectionModeToggle
        connectionMode={connectionMode}
        allowTwimlWebhook
        onConnectionModeChange={(mode) => {
          if (mode === WebSocketConnectionMode.TWIML_WEBHOOK) {
            onMetadataUpdate({
              ...TWIML_WEBHOOK_MODE_METADATA,
              voice_http_method: simulationMetadata.voice_http_method || "POST",
              voice_http_headers: simulationMetadata.voice_http_headers ?? "{}",
              voice_form_fields: simulationMetadata.voice_form_fields ?? "{}",
            })
          } else if (mode === WebSocketConnectionMode.HTTP_FIRST) {
            onMetadataUpdate({
              connection_mode: mode,
              handshake_ready_message_type: "",
              handshake_requires_session_id: false,
            })
          } else {
            onMetadataUpdate({
              connection_mode: mode,
              handshake_ready_message_type: "session_ready",
              handshake_requires_session_id: true,
            })
          }
        }}
        disabled={disabled}
      />

      {!isTwimlWebhook && (
        <WebSocketPresetSelect
          metadata={effectiveSimulationMetadata}
          onMetadataUpdate={onMetadataUpdate}
          disabled={disabled}
        />
      )}

      {testConnection && (
        <AgentTestConnectionButton
          agentId={testConnection.agentId}
          disabled={testConnection.disabled}
          disabledReason={testConnection.disabledReason}
        />
      )}

      {!isHttpFirst && !isTwimlWebhook && (
        <DirectModeFields
          metadata={effectiveSimulationMetadata}
          onMetadataUpdate={onMetadataUpdate}
          disabled={disabled}
        />
      )}

      {isHttpFirst && (
        <>
          <HttpFirstSetupFields
            metadata={effectiveSimulationMetadata}
            onMetadataUpdate={onMetadataUpdate}
            disabled={disabled}
          />
          <HttpFirstWebSocketFields
            metadata={effectiveSimulationMetadata}
            onMetadataUpdate={onMetadataUpdate}
            disabled={disabled}
          />
        </>
      )}

      {isTwimlWebhook && (
        <>
          <TwimlWebhookFields
            metadata={effectiveSimulationMetadata}
            onMetadataUpdate={onMetadataUpdate}
            disabled={disabled}
          />
          <HttpFirstWebSocketFields
            metadata={effectiveSimulationMetadata}
            onMetadataUpdate={onMetadataUpdate}
            disabled={disabled}
            idPrefix="twiml"
            showInitializationJson={false}
          />
        </>
      )}

      {!hideAdvancedSettings && (
        <AdvancedConfigSection
          metadata={effectiveSimulationMetadata}
          onMetadataUpdate={onMetadataUpdate}
          disabled={disabled}
          isHttpFirst={isHttpFirst || isTwimlWebhook}
        />
      )}
    </div>
  )
}
